import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useOrderHistory } from '@/hooks/useOrderHistory';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ShoppingBag, Star, Users, ThumbsUp, Wallet, Trophy } from 'lucide-react';
import type { UserStats, MyReview } from './types';

const COMPLETED_STATUSES = ['completed', 'closed', 'delivered'];

export default function StatsWidget() {
  const { data: ordersResponse, isLoading: ordersLoading } = useOrderHistory(1, '', { enabled: true });

  const stats = useMemo(() => {
    const results = ordersResponse?.results ?? [];
    const completed = results.filter(o => COMPLETED_STATUSES.includes((o.status ?? '').toLowerCase()));
    const total = ordersResponse?.total_results ?? results.length;
    const totalSpend = completed.reduce((sum, o) => sum + (o.total ?? 0), 0);
    return { total, totalSpend };
  }, [ordersResponse]);

  const { data: profileData, isLoading: profileLoading } = useQuery<UserStats>({
    queryKey: ['/api/users/me'],
  });

  const { data: myReviews, isLoading: reviewsLoading } = useQuery<MyReview[]>({
    queryKey: ['/api/reviews/mine'],
    staleTime: 5 * 60_000,
  });

  const topReview = useMemo((): MyReview | null => {
    if (!myReviews?.length) return null;
    return [...myReviews].sort((a, b) => (b.likesCount ?? 0) - (a.likesCount ?? 0))[0];
  }, [myReviews]);

  const totalLikes = useMemo(
    () => myReviews?.reduce((sum, r) => sum + (r.likesCount ?? 0), 0) ?? 0,
    [myReviews]
  );

  if (ordersLoading || profileLoading || reviewsLoading) {
    return (
      <div className="px-4 grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 rounded-lg" />)}
      </div>
    );
  }

  const statItems = [
    { label: 'Orders', value: stats.total, icon: ShoppingBag },
    { label: 'Reviews', value: profileData?.reviewsCount ?? myReviews?.length ?? 0, icon: Star },
    { label: 'Followers', value: profileData?.followersCount ?? 0, icon: Users },
  ];

  return (
    <div className="px-4 space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {statItems.map(s => {
          const Icon = s.icon;
          return (
            <Card key={s.label}>
              <CardContent className="p-3 text-center space-y-1">
                <Icon className="w-4 h-4 text-muted-foreground mx-auto" />
                <div className="text-xl font-bold leading-tight">{s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <ThumbsUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div>
              <div className="font-bold text-sm">{totalLikes}</div>
              <div className="text-xs text-muted-foreground">Total likes</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div>
              <div className="font-bold text-sm">${stats.totalSpend.toFixed(0)}</div>
              <div className="text-xs text-muted-foreground">Lifetime spend</div>
            </div>
          </CardContent>
        </Card>
      </div>
      {topReview && (
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">Top review</div>
              <div className="text-xs font-medium truncate">
                {topReview.restaurant?.name ?? 'A restaurant'} · {topReview.likesCount ?? 0} likes
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
